import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface WizardProgress {
  id?: string;
  user_id: string;
  tenant_id: string;
  current_step: number;
  completed_steps: number[];
  is_dismissed: boolean;
  wizard_data: Record<string, any>;
}

const WIZARD_STEPS = [
  { id: 1, title: "Product Selection", description: "Choose the product you're selling", route: "/app/products" },
  { id: 2, title: "AI Lead Discovery", description: "Let AI find potential leads", route: "/app/leads" },
  { id: 3, title: "Lead Scoring", description: "Review and prioritize leads", route: "/app/leads" },
  { id: 4, title: "Outreach Setup", description: "Configure multi-channel outreach", route: "/app/outreach" },
  { id: 5, title: "Call Execution", description: "Schedule and make calls", route: "/app/calls" },
  { id: 6, title: "Qualification", description: "Qualify leads with AI assistance", route: "/app/leads" },
  { id: 7, title: "Deal Creation", description: "Create deals from qualified leads", route: "/app/deals" },
  { id: 8, title: "Proposal Generation", description: "Generate AI-powered proposals", route: "/app/proposals" },
  { id: 9, title: "Closing", description: "Close deals and create contracts", route: "/app/contracts" },
];

export function useWizardProgress() {
  const { user, profile } = useAuth();
  const [progress, setProgress] = useState<WizardProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch progress on mount
  useEffect(() => {
    if (!user?.id || !profile?.tenant_id) {
      setIsLoading(false);
      return;
    }

    const fetchProgress = async () => {
      const { data, error } = await supabase
        .from('wizard_progress')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        // Silently handle errors for missing records
      }

      if (data) {
        setProgress({
          ...data,
          completed_steps: (data.completed_steps as number[]) || [],
          wizard_data: (data.wizard_data as Record<string, any>) || {},
        });
      } else {
        // Create initial progress
        const newProgress: WizardProgress = {
          user_id: user.id,
          tenant_id: profile.tenant_id,
          current_step: 1,
          completed_steps: [],
          is_dismissed: false,
          wizard_data: {},
        };
        
        const { data: inserted, error: insertError } = await supabase
          .from('wizard_progress')
          .insert(newProgress)
          .select()
          .single();

        if (!insertError && inserted) {
          setProgress({
            ...inserted,
            completed_steps: (inserted.completed_steps as number[]) || [],
            wizard_data: (inserted.wizard_data as Record<string, any>) || {},
          });
        }
      }
      setIsLoading(false);
    };

    fetchProgress();
  }, [user?.id, profile?.tenant_id]);

  const updateProgress = useCallback(async (updates: Partial<WizardProgress>) => {
    if (!user?.id) return;

    const { error } = await supabase
      .from('wizard_progress')
      .update(updates)
      .eq('user_id', user.id);

    if (!error) {
      setProgress(prev => prev ? { ...prev, ...updates } : null);
    }
  }, [user?.id]);

  const goToStep = useCallback((step: number) => {
    updateProgress({ current_step: step });
  }, [updateProgress]);

  const completeStep = useCallback((step: number) => {
    if (!progress) return;
    
    const newCompleted = progress.completed_steps.includes(step)
      ? progress.completed_steps
      : [...progress.completed_steps, step];
    
    const nextStep = step < 9 ? step + 1 : step;
    
    updateProgress({
      completed_steps: newCompleted,
      current_step: nextStep,
    });
  }, [progress, updateProgress]);

  const dismissWizard = useCallback(() => {
    updateProgress({ is_dismissed: true });
  }, [updateProgress]);

  const showWizard = useCallback(() => {
    updateProgress({ is_dismissed: false });
  }, [updateProgress]);

  const resetWizard = useCallback(() => {
    updateProgress({
      current_step: 1,
      completed_steps: [],
      is_dismissed: false,
      wizard_data: {},
    });
  }, [updateProgress]);

  const saveWizardData = useCallback((key: string, value: any) => {
    if (!progress) return;
    const newData = { ...progress.wizard_data, [key]: value };
    updateProgress({ wizard_data: newData });
  }, [progress, updateProgress]);

  const getStepInfo = useCallback((stepId: number) => {
    return WIZARD_STEPS.find(s => s.id === stepId);
  }, []);

  const currentStepInfo = progress ? getStepInfo(progress.current_step) : null;
  const completionPercentage = progress 
    ? Math.round((progress.completed_steps.length / WIZARD_STEPS.length) * 100)
    : 0;

  return {
    progress,
    isLoading,
    steps: WIZARD_STEPS,
    currentStepInfo,
    completionPercentage,
    goToStep,
    completeStep,
    dismissWizard,
    showWizard,
    resetWizard,
    saveWizardData,
    getStepInfo,
    guidanceLevel: profile?.guidance_level || 'beginner',
  };
}
